<template>
  <transition name="modal">
    <div class="modal-mask">
      <div class="modal-wrapper" @click.self="close">
        <PopupPanel
          dark
          class="modal-container"
          role="dialog"
          aria-modal="true"
          aria-label="Add card"
        >
          <PopupHeader class="modal-header">
            <span class="title">Add card</span>

            <slot name="header">
              <PopupClose
                type="button"
                @click="close"
                class="icon card-close"
                aria-label="Close add card"
                :disabled="submitting"
              />
            </slot>
          </PopupHeader>

          <div class="modal-body">
            <slot name="body">
              <div class="inputs">
                <input
                  type="text"
                  class="name-input"
                  placeholder="Full Name"
                  aria-label="Full name on card"
                  v-model="name"
                />
                <div class="loading" v-if="!isReady && !cardError">
                  Loading...
                </div>
                <div class="card-input" :class="{ focused }" v-show="isReady">
                  <div ref="card" />
                </div>
                <div class="card-errors" role="alert" aria-live="polite">
                  {{ cardError }}
                </div>
              </div>
            </slot>
          </div>
          <PopupFooter
            ><PopupActions class="buttons">
              <button
                class="primary outline dark"
                :disabled="submitting"
                @click="close"
              >
                Cancel
              </button>
              <button
                class="primary"
                :disabled="!isReady || !name || !validCard || submitting"
                @click="submit"
              >
                {{ submitting ? "Submitting..." : "Add" }}
              </button>
            </PopupActions></PopupFooter
          >
        </PopupPanel>
      </div>
    </div>
  </transition>
</template>

<script>
import dialogFocus from "@/mixins/dialogFocus";

import ADD_PAYMENT_METHOD from "@/api/mutations/ADD_PAYMENT_METHOD.gql";
import GET_PAYMENT_METHODS from "@/api/queries/GET_PAYMENT_METHODS.gql";
import ADD_EXTERNAL_ACCOUNT from "@/api/mutations/ADD_EXTERNAL_ACCOUNT.gql";
import GET_EXTERNAL_ACCOUNTS from "@/api/queries/GET_EXTERNAL_ACCOUNTS.gql";
import GET_STRIPE_CONFIG from "@/api/queries/GET_STRIPE_CONFIG.gql";

export default {
  name: "AddCard",
  mixins: [dialogFocus],
  components: {},
  props: {
    isOnboarding: Boolean,
    isPayouts: Boolean,
    chargesEnabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      submitting: false,
      validCard: false,
      focused: false,
      isReady: false,
      cardError: "",
      name: ""
    };
  },
  async mounted() {
    this._cardDisposed = false;
    try {
      const { data } = await this.$apollo.query({
        query: GET_STRIPE_CONFIG,
        fetchPolicy: "no-cache"
      });
      if (this._cardDisposed) return;
      const config = data && data.stripe_public_config;
      const key = config && config.publishableKey;
      const mode = /^pk_(test|live)_[A-Za-z0-9]+$/.exec(key || "");
      if (
        !config ||
        !config.enabled ||
        !mode ||
        mode[1] !== config.mode ||
        typeof window.Stripe !== "function"
      ) {
        throw new Error("Card configuration unavailable");
      }
      this._stripe = window.Stripe(key);
      const elements = this._stripe.elements();
      this._card = elements.create("card", {
        style: {
          base: {
            fontSize: "14px",
            lineHeight: "14px",
            fontFamily: "sofia-pro, sans-serif"
          }
        }
      });
      this._card.mount(this.$refs.card);
      this._card.on("change", e => {
        this.validCard = e.complete;
        this.cardError = e.error ? e.error.message : "";
      });
      this._card.on("ready", () => {
        this.isReady = true;
      });
      this._card.on("focus", () => {
        this.focused = true;
      });
      this._card.on("blur", () => {
        this.focused = false;
      });
    } catch (_) {
      if (this._cardDisposed) return;
      this.cardError =
        "Card setup is unavailable. Please close this window and try again later.";
    }
  },
  beforeDestroy() {
    this._cardDisposed = true;
    this.destroyCard();
  },
  methods: {
    requestClose() {
      this.close();
    },
    destroyCard() {
      if (this._card) this._card.destroy();
      this._card = null;
    },
    close() {
      if (this.submitting) return;
      this._cardDisposed = true;
      this.destroyCard();
      this.$emit("close");
    },
    async getToken(extraDetails) {
      const result = await this._stripe.createToken(this._card, extraDetails);
      if (result && result.error) {
        const error = new Error(
          result.error.message || "Please check your card details."
        );
        error.isCardValidation = true;
        throw error;
      }
      if (
        !result ||
        !result.token ||
        result.token.type !== "card" ||
        !result.token.card
      ) {
        throw new Error(
          "Unable to validate this card. Please check your card details."
        );
      }
      return { id: result.token.id, added: result.token.card };
    },
    async addCard({ id }) {
      await this.$apollo.mutate({
        mutation: ADD_PAYMENT_METHOD,
        variables: { token: id },
        update: async (store, { data: { stripe_add_payment_method } }) => {
          if (!this.isOnboarding) {
            const data = store.readQuery({ query: GET_PAYMENT_METHODS });
            data.stripe_get_payment_methods = [
              ...data.stripe_get_payment_methods,
              stripe_add_payment_method
            ];
            await store.writeQuery({ query: GET_PAYMENT_METHODS, data });
          }

          this.submitting = false;
          this.destroyCard();

          this.$emit("close", stripe_add_payment_method);
        }
      });
    },
    async addExternalAccount({ id, added }) {
      if (added.funding !== "debit") {
        this.$errorService.showError(
          "Only debit cards are allowed for payouts.",
          "Card Type Error"
        );
        this.submitting = false;
        this._card.clear();
        throw new Error("Only debit cards are allowed for payouts.");
      }

      await this.$apollo.mutate({
        mutation: ADD_EXTERNAL_ACCOUNT,
        variables: { token: id },
        update: async (store, { data: { stripe_add_external_account } }) => {
          const data = store.readQuery({
            query: GET_EXTERNAL_ACCOUNTS
          });

          data.stripe_get_external_accounts = [
            ...data.stripe_get_external_accounts,
            stripe_add_external_account
          ];

          return store.writeQuery({
            query: GET_EXTERNAL_ACCOUNTS,
            data
          });
        }
      });
    },
    async submit() {
      if (this.submitting || !this.isReady || !this.validCard || !this.name)
        return;
      try {
        this.submitting = true;
        this.cardError = "";
        const extraDetails = {
          name: this.name,
          currency: "usd"
        };

        let token = await this.getToken(extraDetails);

        if (
          this.chargesEnabled &&
          (this.isPayouts || token.added.funding === "debit")
        ) {
          await this.addExternalAccount(token);
          if (!this.isPayouts) token = await this.getToken(extraDetails);
          else {
            this.submitting = false;
            this.destroyCard();
            return this.$emit("close");
          }
        }

        return await this.addCard(token);
      } catch (err) {
        this.cardError = err.isCardValidation
          ? err.message
          : "Unable to add this card. Check your saved payment methods before trying again.";
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/styles/_mixins.scss";

.card-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  background: transparent;
  border: 0;
}
.card-close:focus-visible {
  outline: 2px solid $accent;
  outline-offset: 2px;
}

.modal-container {
  --popup-max-width: 600px;
  max-width: 600px;
  width: calc(100% - 32px);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  height: fit-content;
  margin: 16px auto;
}

.modal-header {
  display: flex;
  justify-content: flex-end;
  padding: 32px 32px 0;
}

.modal-body {
  padding: 0 34px 60px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  .title {
    margin-bottom: 30px;
    font-size: 1.5rem;
    color: $white;
  }
  .inputs {
    min-width: 0;
    width: 100%;
    max-width: 26rem;
    .loading,
    .name-input {
      color: #555;
      font-size: 0.9rem;
      height: 42px;
      line-height: 12px;
      font-size: 14px;
      margin-bottom: 8px;
      background-color: #eee;
      padding: 13px 23px;
      border-radius: 38px;
      border: 1px #eee solid;
      width: 100%;
      &:focus {
        outline: 2px solid $accent;
        outline-offset: 2px;
      }
    }
    .card-input {
      margin-bottom: 8px;
      background-color: #eee;
      padding: 13px;
      border-radius: 38px;
      border: 1px #eee solid;
    }
    .focused {
      border: 1px $accent solid;
    }
    .card-errors {
      color: $accent;
      font-size: 0.9rem;
      overflow-wrap: anywhere;
    }
  }
  .buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;
    margin-top: 30px;
    button {
      margin: 0;
      min-width: 100px;
      min-height: 44px;
    }
  }
}
@media (max-width: 480px) {
  .modal-header {
    padding: 16px 16px 0;
  }
  .modal-body {
    padding: 0 16px 24px;
  }
}
</style>
